import Quill from 'quill';
import { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { NumberInput } from '@hivebrite/design-system/Inputs';

import { useQuillEditorContext } from 'hb-react/shared/components/DesignSystem/QuillEditor/contexts/QuillEditorContext';
import { MAX_SIZE } from 'hb-react/shared/components/DesignSystem/QuillEditor/formats/Size';
import { ToolbarItemWrapper } from 'hb-react/shared/components/DesignSystem/QuillEditor/Toolbar/components/ToolbarItem';

const FontSize = () => {
  const { quillRef, format } = useQuillEditorContext();
  const inputRef = useRef<HTMLInputElement>(null);

  const [isFocus, setIsFocus] = useState(false);

  const defaultValue =
    quillRef.current &&
    parseInt(window.getComputedStyle(quillRef.current.container).fontSize, 10);

  const quillSelectionSize =
    (format?.size && parseInt(format?.size, 10)) || defaultValue || '';

  const [value, setValue] = useState(quillSelectionSize);
  const [applyNextChange, setApplyNextChange] = useState(false);

  // Update the size based on the current selection in quill
  useEffect(() => {
    if (isFocus) {
      return;
    }

    if (!quillSelectionSize) {
      return;
    }

    setValue(quillSelectionSize);
  }, [isFocus, quillSelectionSize]);

  const handleFocus = () => {
    setIsFocus(true);
  };

  const handleBlur = () => {
    setIsFocus(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && value) {
      quillRef.current?.format('size', `${value}px`, Quill.sources.USER);
      inputRef.current?.blur();
      e.preventDefault();
    }

    if (e.key === 'Escape') {
      inputRef.current?.blur();
      e.preventDefault();
    }

    if (e.key === 'ArrowUp') {
      setApplyNextChange(true);
    }

    if (e.key === 'ArrowDown') {
      setApplyNextChange(true);
    }
  };

  const handleChangeFontSize = useCallback(
    (e) => {
      if (!isFocus) return;

      const val = e.target?.value;

      if (!val) {
        return setValue('');
      }

      if (Number.isNaN(val)) {
        return;
      }

      const newSize = Math.min(val, MAX_SIZE);

      setValue(newSize);

      if (applyNextChange) {
        setApplyNextChange(false);
        quillRef.current?.format('size', `${newSize}px`, Quill.sources.USER);
        inputRef.current?.focus();
      }
    },
    [applyNextChange, isFocus, quillRef],
  );

  return (
    <ToolbarItemWrapper onKeyDown={handleKeyPress}>
      <NumberInput
        id="font-size"
        name="font-size"
        ariaLabel={__('Font size')}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChangeFontSize}
        ref={inputRef}
        value={value}
        variant="small"
        width={60}
      />
      <Px>px</Px>
    </ToolbarItemWrapper>
  );
};

export default FontSize;

const Px = styled(Box).attrs({
  alignItems: 'center',
  height: '100%',
  fontSize: 2,
  ml: 1,
})`
  cursor: initial;
`;
